import React from 'react';
import ReactDOM from 'react-dom';
import { Handle, Position } from 'reactflow';
import { useParams } from 'react-router-dom';
import { Database, Inbox, CheckCircle, XCircle, Clock, Trash2, Sparkles, Play, ExternalLink } from 'lucide-react';
import { NodeData } from '../../../types';
import { useProjectStore, useFlowStore } from '../../../store';
import { useContextualNavigation } from '../../../hooks/useContextualNavigation';
import ModelDetailsModal from '../../models/ModelDetailsModal';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import { NodeLabel, NodeBadge } from './shared';
import { useEntityEvent } from '../../../hooks/useEntityEvent';
import { useQueue, clearCachedQueue } from '../../../hooks/useQueue';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';
import { useModels } from '../../../hooks/queries/useModels';

interface QueueReadNodeProps {
  data: NodeData;
  selected: boolean;
}

const QueueReadNode: React.FC<QueueReadNodeProps> = ({ data, selected }) => {
  // AC-1412 / AC-1377 P5.1: project models flow through React Query
  // (`useModels`) instead of `useProjectStore.models`.
  const currentProjectId = useProjectStore((state) => state.currentProject?.project_id);
  const modelsQuery = useModels(currentProjectId);
  const models = React.useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const { nodes, edges, setNodes, setEdges, setSelectedNode, selectedNode, isModelModalOpen, setIsModelModalOpen } = useFlowStore();

  // AC-1406: Delete via React Query mutation hook.
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');
  const { navigateWithContext } = useContextualNavigation();
  const { projectId } = useParams<{ projectId: string }>();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // Handler to open queue details page
  const handleOpenQueue = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent node selection
    if (queue?.queue_id && projectId) {
      navigateWithContext(`/projects/${projectId}/queues/${queue.queue_id}`, 'Workflow');
    }
  };

  // Check if node can be deleted (not referenced by other nodes)
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      n =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id)
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id]);

  // Read the configured source queue id once - both the mount fetch and the
  // entity-event invalidation listener need it.
  const sourceQueueId = (data.step?.config as { source_queue_id?: number } | undefined)?.source_queue_id;

  const { queue, isLoading: isLoadingQueue } = useQueue(sourceQueueId);

  // AC-1369: clear the cached queue immediately when the referenced queue is
  // deleted elsewhere, instead of waiting for an incidental re-render.
  useEntityEvent({
    entity: 'queue',
    id: sourceQueueId,
    handler: (event) => {
      if (event.kind === 'deleted' && sourceQueueId !== undefined) {
        clearCachedQueue(sourceQueueId);
      }
    },
  });

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;

    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });

      // Remove the node and its connected edges from local canvas state
      // for an immediate UX response. The mutation hook's invalidation
      // refreshes the steps cache; the seed effect will rebuild from it.
      setNodes(nodes.filter(node => node.id !== data.id));
      setEdges(edges.filter(edge => edge.source !== data.id && edge.target !== data.id));

      setSelectedNode(null);
    } catch (error) {
      console.error('Failed to delete step:', error);
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Clock className="w-4 h-4 text-primary-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  // Check if this is the first step (initiator)
  const isFirstStep = !data.step?.previous_step_id;

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return 'bg-blue-50 dark:bg-blue-900/80';
      case 'completed':
        return 'bg-green-50 dark:bg-green-900/80';
      case 'failed':
        return 'bg-red-50 dark:bg-red-900/80';
      default:
        return 'bg-purple-50 dark:bg-gray-800';
    }
  };

  const getBorderColor = () => {
    if (isFirstStep) {
      return 'border-green-500';
    }
    switch (data.status) {
      case 'running':
        return 'border-blue-500';
      case 'completed':
        return 'border-green-500';
      case 'failed':
        return 'border-red-500';
      default:
        return 'border-purple-300';
    }
  };

  const config = data.step?.config as {
    source_queue_id?: number;
    consumption_mode?: 'single' | 'batch';
    batch_size?: number;
    visibility_timeout_seconds?: number;
  } | undefined;

  const queueName = queue?.name || (config?.source_queue_id ? `Queue #${config.source_queue_id}` : 'No queue selected');
  const consumptionMode = config?.consumption_mode || 'single';
  const batchSize = config?.batch_size || 50;
  const visibilityTimeout = config?.visibility_timeout_seconds || 300;

  const formatTimeout = (seconds: number): string => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const handleModelBadgeClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Set selectedNode to this node's ID so only THIS node renders the modal
    setSelectedNode(data.step?.orchestration_step_id || null);
    setIsModelModalOpen(true);
  }, [setSelectedNode, setIsModelModalOpen, data.step?.orchestration_step_id]);

  const handleCloseModelDetails = React.useCallback(() => {
    setIsModelModalOpen(false);
  }, [setIsModelModalOpen]);

  const queueModel = queue?.data_model_id 
    ? models.find(m => m.model_id === queue.data_model_id)
    : undefined;

  return (
    <div className="relative">
      <div
        className={`relative w-72 h-32 px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          ${getBorderColor()}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {/* Start badge - show when this is the first step, clickable to start execution.
            Positioned outside the card (top-left corner) so it doesn't overlap the queue name. */}
        {isFirstStep && (
          <button
              title="Click to start orchestration"
              onClick={(e) => {
                e.stopPropagation();
                data.onStartExecution?.();
              }}
              className="absolute -top-3 -left-2 px-2 py-1 rounded-full bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold shadow-md whitespace-nowrap z-20 flex items-center gap-1 cursor-pointer hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 transition-all duration-150"
            >
              <Play className="w-3 h-3" />
              <span>Start</span>
            </button>
        )}

        {/* Delete button - only show if node is deletable */}
        {isDeletable && (
          <button
              title="Delete step"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        )}

        {/* Source handles for dynamic edge connections (queue_read is initiator) */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />

        {/* Queue Read Badge - Purple gradient for initiator */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4" />
            <span>Queue Read</span>
            {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
              <Tooltip content="Feedback Loop Hints configured">
                <div>
                  <Sparkles className="w-3 h-3" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Main Card Content */}
        <div className="flex flex-col items-center py-2 w-full">
          <div className="flex items-center gap-2 mb-2 w-full justify-center">
            <NodeLabel
              label={isLoadingQueue ? 'Loading...' : queueName}
              maxLength={20}
              fontWeight="medium"
              textColor="text-gray-900 dark:text-warm-white"
            />
            {getStatusIcon()}
            {queue && (
              <Tooltip content="Open queue details">
                <button
                  onClick={handleOpenQueue}
                  className="text-primary-600 hover:text-primary-800 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>

          {/* Queue Info Display */}
          <div className="px-3 py-2 rounded bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs border border-purple-200 dark:border-purple-700 mx-2 self-stretch">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Database className="w-3 h-3" />
                <span className="font-medium">
                  {consumptionMode === 'batch' ? `Batch: ${batchSize}` : 'Single'}
                </span>
              </div>
              <div className="text-purple-600 dark:text-purple-400">
                Timeout: {formatTimeout(visibilityTimeout)}
              </div>
            </div>
          </div>

          {/* Model Badge */}
          {queueModel && (
            <NodeBadge
              icon={<Database className="w-3 h-3" />}
              label={queueModel.class_name || 'Model'}
              variant="model"
              onClick={handleModelBadgeClick}
              tooltip="Click to view model details"
              className="mt-1"
            />
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Model Details Popup rendered via portal - only render if THIS node opened the modal */}
      {isModelModalOpen && queueModel && selectedNode === data.step?.orchestration_step_id && ReactDOM.createPortal(
        <ModelDetailsModal
          model={queueModel}
          allModels={models}
          onClose={handleCloseModelDetails}
          onEdit={() => {}}
        />,
        document.getElementById('modal-root') as HTMLElement
      )}
    </div>
  );
};

export default QueueReadNode;
